import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Select,
  Input,
  Space,
  Modal,
  Form,
  message,
  Badge,
  Tooltip,
  Row,
  Col,
  Statistic,
  Empty
} from 'antd';
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  FilterOutlined,
  SearchOutlined,
  BellOutlined
} from '@ant-design/icons';
import { environmentService, EnvironmentAlert } from '../../../services/environmentService';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;
const { TextArea } = Input;

interface EnvironmentAlertsProps {
  onRefresh?: () => void;
}

const EnvironmentAlerts: React.FC<EnvironmentAlertsProps> = ({ onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState<EnvironmentAlert[]>([]);
  const [filteredAlerts, setFilteredAlerts] = useState<EnvironmentAlert[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<EnvironmentAlert | null>(null);
  const [acknowledgeModalVisible, setAcknowledgeModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    acknowledged: '',
    search: ''
  });
  const [form] = Form.useForm();

  // 載入警報數據
  const loadAlerts = async () => {
    try {
      setLoading(true);
      const data = await environmentService.getAlerts();
      setAlerts(data);
      setFilteredAlerts(data);
    } catch (error) {
      console.error('載入警報數據失敗:', error);
      message.error('載入警報數據失敗');
    } finally {
      setLoading(false);
    }
  };

  // 過濾警報
  const filterAlerts = () => {
    let filtered = [...alerts];

    if (filters.type) {
      filtered = filtered.filter(alert => alert.type === filters.type);
    }

    if (filters.severity) {
      filtered = filtered.filter(alert => alert.severity === filters.severity);
    }

    if (filters.acknowledged) {
      const isAcknowledged = filters.acknowledged === 'true';
      filtered = filtered.filter(alert => !!alert.acknowledged === isAcknowledged);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(alert =>
        alert.message.toLowerCase().includes(searchLower) ||
        alert.location.area.toLowerCase().includes(searchLower)
      );
    }

    setFilteredAlerts(filtered);
  };

  // 處理過濾器變更
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // 清除過濾器
  const clearFilters = () => {
    setFilters({
      type: '',
      severity: '',
      acknowledged: '',
      search: ''
    });
  };

  // 確認處理警報
  const handleAcknowledgeAlert = async (values: any) => {
    if (!selectedAlert?._id) return;

    try {
      await environmentService.acknowledgeAlert(
        selectedAlert._id,
        values.acknowledgedBy,
        values.notes
      );
      
      message.success('警報已標記為已處理');
      setAcknowledgeModalVisible(false);
      setSelectedAlert(null);
      form.resetFields();
      await loadAlerts();
      onRefresh?.();
    } catch (error) {
      console.error('處理警報失敗:', error);
      message.error('處理警報失敗');
    }
  };

  // 獲取嚴重程度標籤
  const getSeverityTag = (severity: string) => {
    const config = {
      critical: { color: 'red', text: '嚴重' },
      warning: { color: 'orange', text: '警告' },
      info: { color: 'blue', text: '資訊' }
    };
    const { color, text } = config[severity as keyof typeof config] || config.info;
    return <Tag color={color}>{text}</Tag>;
  };

  // 獲取類型標籤
  const getTypeTag = (type: string) => {
    const config = {
      air_quality: { color: 'green', text: '空氣品質' },
      lighting: { color: 'blue', text: '照明系統' },
      noise: { color: 'purple', text: '噪音監測' },
      energy: { color: 'gold', text: '能源管理' }
    };
    const { color, text } = config[type as keyof typeof config] || { color: 'default', text: type };
    return <Tag color={color}>{text}</Tag>;
  };

  // 表格列定義
  const columns: ColumnsType<EnvironmentAlert> = [
    {
      title: '時間',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (timestamp: string) => new Date(timestamp).toLocaleString('zh-TW'),
      sorter: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      defaultSortOrder: 'descend'
    },
    {
      title: '類型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => getTypeTag(type),
      filters: [
        { text: '空氣品質', value: 'air_quality' },
        { text: '照明系統', value: 'lighting' },
        { text: '噪音監測', value: 'noise' },
        { text: '能源管理', value: 'energy' }
      ],
      onFilter: (value, record) => record.type === value
    },
    {
      title: '嚴重程度',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity: string) => getSeverityTag(severity),
      filters: [
        { text: '嚴重', value: 'critical' },
        { text: '警告', value: 'warning' },
        { text: '資訊', value: 'info' }
      ],
      onFilter: (value, record) => record.severity === value
    },
    {
      title: '位置',
      key: 'location',
      width: 150,
      render: (_, record) => (
        <span>
          {record.location.area}
          {record.location.floor && ` - ${record.location.floor}F`}
          {record.location.zone && ` - ${record.location.zone}`}
        </span>
      )
    },
    {
      title: '警報訊息',
      dataIndex: 'message',
      key: 'message',
      ellipsis: {
        showTitle: false
      },
      render: (message: string) => (
        <Tooltip title={message}>
          {message}
        </Tooltip>
      )
    },
    {
      title: '狀態',
      key: 'status',
      width: 100,
      render: (_, record) => (
        record.acknowledged ? (
          <Badge status="success" text="已處理" />
        ) : (
          <Badge status="error" text="待處理" />
        )
      ),
      filters: [
        { text: '已處理', value: 'true' },
        { text: '待處理', value: 'false' }
      ],
      onFilter: (value, record) => (!!record.acknowledged).toString() === value
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          {!record.acknowledged && (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => {
                setSelectedAlert(record);
                setAcknowledgeModalVisible(true);
              }}
            >
              處理
            </Button>
          )}
        </Space>
      )
    }
  ];

  // 統計數據
  const getAlertStats = () => {
    const total = alerts.length;
    const critical = alerts.filter(a => a.severity === 'critical').length;
    const warning = alerts.filter(a => a.severity === 'warning').length;
    const unacknowledged = alerts.filter(a => !a.acknowledged).length;

    return { total, critical, warning, unacknowledged };
  };

  useEffect(() => {
    loadAlerts();
  }, []);

  useEffect(() => {
    filterAlerts();
  }, [filters, alerts]);

  const stats = getAlertStats();

  return (
    <div className="environment-alerts">
      {/* 統計概覽 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="總警報數"
              value={stats.total}
              prefix={<BellOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="嚴重警報"
              value={stats.critical}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="警告"
              value={stats.warning}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="待處理"
              value={stats.unacknowledged}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 過濾器 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={4}>
            <Select
              placeholder="警報類型"
              value={filters.type}
              onChange={(value) => handleFilterChange('type', value)}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="air_quality">空氣品質</Option>
              <Option value="lighting">照明系統</Option>
              <Option value="noise">噪音監測</Option>
              <Option value="energy">能源管理</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="嚴重程度"
              value={filters.severity}
              onChange={(value) => handleFilterChange('severity', value)}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="critical">嚴重</Option>
              <Option value="warning">警告</Option>
              <Option value="info">資訊</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="處理狀態"
              value={filters.acknowledged}
              onChange={(value) => handleFilterChange('acknowledged', value)}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="false">待處理</Option>
              <Option value="true">已處理</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Input
              placeholder="搜尋警報訊息或位置"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col span={6}>
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={clearFilters}
              >
                清除過濾器
              </Button>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={loadAlerts}
                loading={loading}
              >
                刷新
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 警報列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredAlerts}
          rowKey="_id"
          loading={loading}
          pagination={{
            total: filteredAlerts.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 項，共 ${total} 項`
          }}
          locale={{
            emptyText: <Empty description="暫無警報數據" />
          }}
        />
      </Card>

      {/* 處理警報模態框 */}
      <Modal
        title="處理警報"
        open={acknowledgeModalVisible}
        onCancel={() => {
          setAcknowledgeModalVisible(false);
          setSelectedAlert(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        {selectedAlert && (
          <div>
            <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 6 }}>
              <p><strong>警報類型:</strong> {getTypeTag(selectedAlert.type)}</p>
              <p><strong>嚴重程度:</strong> {getSeverityTag(selectedAlert.severity)}</p>
              <p><strong>位置:</strong> {selectedAlert.location.area} - {selectedAlert.location.floor}F</p>
              <p><strong>訊息:</strong> {selectedAlert.message}</p>
              <p><strong>時間:</strong> {new Date(selectedAlert.timestamp).toLocaleString('zh-TW')}</p>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleAcknowledgeAlert}
            >
              <Form.Item
                name="acknowledgedBy"
                label="處理人員"
                rules={[{ required: true, message: '請輸入處理人員姓名' }]}
              >
                <Input placeholder="請輸入處理人員姓名" />
              </Form.Item>

              <Form.Item
                name="notes"
                label="處理備註"
                rules={[{ required: true, message: '請輸入處理備註' }]}
              >
                <TextArea
                  rows={4}
                  placeholder="請描述處理過程和結果..."
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => {
                    setAcknowledgeModalVisible(false);
                    setSelectedAlert(null);
                    form.resetFields();
                  }}>
                    取消
                  </Button>
                  <Button type="primary" htmlType="submit">
                    確認處理
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EnvironmentAlerts;